import { ExpandableText } from "../components/ExpandableText";
import { useCallback, useEffect, useState } from "react";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import { FolderOpen, ShieldCheck } from "lucide-react";
import { useParams } from "react-router-dom";
import type { Post, PostCategory, UserProfile } from "../../shared/contracts";
import { PostCard } from "../components/PostCard";
import { StatePanel } from "../components/StatePanel";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { initials } from "../lib/format";

export function SharedCategoryPage() {
  const { shareToken = "" } = useParams();
  const { config } = useApp();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [category, setCategory] = useState<PostCategory | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api<{ user: UserProfile; category: PostCategory; items: Post[] }>(`/users/share/categories/${shareToken}`);
      setProfile(result.user);
      setCategory(result.category);
      setPosts(result.items);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "分享分类加载失败");
    } finally {
      setLoading(false);
    }
  }, [shareToken]);

  useEffect(() => { void load(); }, [load]);

  return <main className="shared-page"><header className="shared-page-brand"><ShieldCheck aria-hidden="true" /><span><strong>{config?.site.name ?? "共享朋友圈"}</strong><small>分类分享视图</small></span></header><div className="shared-page-content">
    {loading ? <StatePanel kind="loading" title="正在打开分类分享" /> : null}
    {!loading && (error || !profile || !category) ? <StatePanel kind="error" title="分享分类无法打开" description={error ?? "链接可能已失效"} onRetry={() => void load()} /> : null}
    {profile && category ? <>
      <header className={`profile-header shared-profile-header shared-category-header${profile.backgroundUrl ? " has-background" : ""}`} style={profile.backgroundUrl ? { backgroundImage: `linear-gradient(rgba(16,17,22,.28), rgba(16,17,22,.74)), url(${profile.backgroundUrl})` } : undefined}>
        <div className="profile-identity"><Avatar className="avatar avatar-large" src={profile.avatarUrl ?? undefined}>{initials(profile.nickname)}</Avatar><div><div className="tag-row"><Chip color="primary" label={profile.role === "seller" ? "号商" : profile.role === "admin" ? "管理员" : "玩家"} /></div><h1>{profile.nickname}</h1><ExpandableText key={profile.id} text={profile.bio || "这位用户还没有填写个人简介。"} /></div></div>
        <div className="shared-category-heading"><span className="shared-category-icon"><FolderOpen aria-hidden="true" /></span><div><span className="eyebrow">分享分类</span><h2>{category.name}</h2><p>{posts.length} 条公开动态</p></div></div>
      </header>
      <p className="shared-page-boundary">此页面只展示“{category.name}”分类中的公开动态，私有、待审核或已移入仓库的内容不会对外展示。</p>
      <section className="shared-category-feed" aria-labelledby="shared-category-feed-title"><div className="section-heading"><div><span className="eyebrow">分类内容</span><h2 id="shared-category-feed-title">{category.name}</h2></div><span>{posts.length} 条结果</span></div><div className="shared-profile-feed">{posts.length ? posts.map((post) => <PostCard key={post.id} initialPost={post} compact isolated />) : <StatePanel kind="empty" title="该分类暂时没有公开动态" description="分类中的共享动态通过审核并公开后会显示在这里。" />}</div></section>
    </> : null}
  </div><footer className="shared-page-footer">独立分类分享页 · 不提供站内内容发现入口</footer></main>;
}
